import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Card, Tabs, Table, Tag, Button, Input, Statistic, Row, Col, Select,
} from "antd";
import {
  UserOutlined, BankOutlined, PlusOutlined, EyeOutlined, FilterOutlined,
} from "@ant-design/icons";
import userService from "../../api/userService";

const { Search } = Input;
const { Option } = Select;

const FilterBar = ({ q, setQ, commune, setCommune, profileComplete, setProfileComplete, searchPlaceholder, showProfile = true }) => (
  <Row gutter={[8, 8]} style={{ marginBottom: 12 }} align="middle">
    <Col xs={24} sm={10}>
      <Search
        placeholder={searchPlaceholder}
        allowClear
        value={q}
        onChange={(e) => setQ(e.target.value || undefined)}
      />
    </Col>
    <Col xs={24} sm={7}>
      <Input
        prefix={<FilterOutlined style={{ color: "#aaa" }} />}
        placeholder="Filtrar por comuna"
        allowClear
        value={commune}
        onChange={(e) => setCommune(e.target.value || undefined)}
      />
    </Col>
    {showProfile && (
      <Col xs={24} sm={7}>
        <Select
          style={{ width: "100%" }}
          placeholder="Estado de perfil"
          allowClear
          value={profileComplete}
          onChange={(v) => setProfileComplete(v)}
        >
          <Option value="true">Completo</Option>
          <Option value="false">Pendiente</Option>
        </Select>
      </Col>
    )}
  </Row>
);

const PersonasTab = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [q, setQ] = useState();
  const [commune, setCommune] = useState();
  const [profileComplete, setProfileComplete] = useState();
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const extraFilters = {};
      if (commune) extraFilters.commune = commune;
      if (profileComplete !== undefined) extraFilters.profileComplete = profileComplete;
      const res = await userService.getUsers(limit, page, { role: "client", entityType: "person", ...extraFilters }, q);
      setTotal(res.data.totalResults);
      setUsers(res.data.results);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [page, limit, q, commune, profileComplete]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [q, commune, profileComplete]);
  useEffect(() => { fetch(); }, [fetch]);

  const columns = [
    {
      title: "Nombre",
      key: "name",
      render: (_, r) => [r.name, r.firstLastName, r.secondLastName].filter(Boolean).join(" ") || "—",
    },
    { title: "RUT", dataIndex: "rut", key: "rut" },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true,
      render: (v) => v?.includes('@temp.digital-notary.cl') || v?.includes('@servel.digital-notary.cl') ? <span style={{ color: "#aaa" }}>—</span> : v },
    { title: "Teléfono", dataIndex: "phone", key: "phone" },
    { title: "Comuna", dataIndex: "commune", key: "commune" },
    {
      title: "Perfil",
      dataIndex: "profileComplete",
      key: "profileComplete",
      render: (v) => <Tag color={v ? "green" : "orange"}>{v ? "Completo" : "Pendiente"}</Tag>,
    },
    {
      title: "",
      key: "actions",
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/admin/users/${r.id}`)}>
          Ver
        </Button>
      ),
    },
  ];

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col>
          <Statistic title="Total personas" value={total} prefix={<UserOutlined />} />
        </Col>
      </Row>
      <FilterBar
        q={q} setQ={setQ}
        commune={commune} setCommune={setCommune}
        profileComplete={profileComplete} setProfileComplete={setProfileComplete}
        searchPlaceholder="Buscar por nombre, RUT, email, teléfono..."
      />
      <Table
        rowKey="id"
        columns={columns}
        dataSource={users}
        loading={loading}
        pagination={{ current: page, pageSize: limit, total, showSizeChanger: true, onChange: (p, s) => { setPage(p); setLimit(s); } }}
        size="small"
      />
    </>
  );
};

const EmpresasTab = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [q, setQ] = useState();
  const [commune, setCommune] = useState();
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const extraFilters = {};
      if (commune) extraFilters.commune = commune;
      const res = await userService.getUsers(limit, page, { role: "client", entityType: "company", ...extraFilters }, q);
      setTotal(res.data.totalResults);
      setCompanies(res.data.results);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [page, limit, q, commune]);

  useEffect(() => { setPage(1); }, [q, commune]);
  useEffect(() => { fetch(); }, [fetch]);

  const columns = [
    {
      title: "Razón social",
      dataIndex: "companyName",
      key: "companyName",
      render: (v, r) => v || r.name || "—",
    },
    { title: "RUT empresa", dataIndex: "companyRut", key: "companyRut" },
    { title: "Giro", dataIndex: "companyGiro", key: "companyGiro", ellipsis: true },
    {
      title: "Representante legal",
      key: "legalRep",
      render: (_, r) => r.legalRepName
        ? `${r.legalRepName} (${r.legalRepRut || ""})`
        : [r.name, r.firstLastName].filter(Boolean).join(" ") || "—",
    },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true },
    {
      title: "",
      key: "actions",
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/admin/users/${r.id}`)}>
          Ver
        </Button>
      ),
    },
  ];

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col>
          <Statistic title="Total empresas" value={total} prefix={<BankOutlined />} />
        </Col>
      </Row>
      <FilterBar
        q={q} setQ={setQ}
        commune={commune} setCommune={setCommune}
        profileComplete={undefined} setProfileComplete={() => {}}
        searchPlaceholder="Buscar por razón social, RUT empresa, email..."
        showProfile={false}
      />
      <Table
        rowKey="id"
        columns={columns}
        dataSource={companies}
        loading={loading}
        pagination={{ current: page, pageSize: limit, total, showSizeChanger: true, onChange: (p, s) => { setPage(p); setLimit(s); } }}
        size="small"
      />
    </>
  );
};

const ClientsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "personas";

  const tabItems = [
    {
      key: "personas",
      label: <span><UserOutlined /> Personas</span>,
      children: <PersonasTab />,
    },
    {
      key: "empresas",
      label: <span><BankOutlined /> Empresas</span>,
      children: <EmpresasTab />,
    },
  ];

  return (
    <Card
      style={{ margin: 10 }}
      title="Clientes"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/admin/users/new")}
        >
          Nuevo cliente
        </Button>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key })}
        items={tabItems}
        size="large"
      />
    </Card>
  );
};

export default ClientsPage;
